/**
 * Whether saving a procedure step should touch the calendar, and with whom.
 *
 * There are two ways to date a schedulable procedure and until now only one of
 * them reached the calendar. The hub's scheduling row opens the appointment flow,
 * which creates a real appointment and mirrors the date back onto the phase. The
 * step's own modal writes the phase and nothing else — so a patient who typed the
 * date into the step had it stored correctly and saw it nowhere near her
 * calendar, while the app had told her, on that very card, that scheduling it
 * would keep her calendar up to date.
 *
 * Extracted from the screen so the rules are testable without rendering it, the
 * same way `endCycleOutcomeMerge` is. It decides; the caller performs.
 *
 * Deliberately says nothing about a date that has already passed. An appointment
 * is a thing you are going to; back-filling one for a collection that happened
 * last month puts a reminder in the past and clutters the calendar with events
 * nobody can attend. The step record is the right home for what already
 * happened, and it already holds it.
 */

import { calculateAppointmentEnd } from "../../care/appointment-duration";

/**
 * What the app itself offers as an appointment's default length, and the hour it
 * falls back to when she gave a date and no time.
 *
 * The backend refuses an appointment without `startTime`, `endDate` and
 * `endTime` — `mergeAppointmentFormats` fills in nothing, and
 * `createAppointment` throws `ValidationError` on any of the three. A calendar
 * cannot hold a timeless event, so a date-only collection gets a stated hour it
 * can be corrected from rather than no calendar entry at all. 60 minutes is
 * `AddAppointmentModal`'s own default; 9am is what `useCreateAppointment`
 * already assumes when it has no time to work with.
 */
const DEFAULT_DURATION_MINUTES = 60;
const DEFAULT_START_TIME = "09:00";

export type ProcedureAppointmentPlan = {
  /**
   * `update` when the step already names an appointment — the patient is
   * correcting a date she has already put in her calendar, and a second event is
   * not what she meant. `none` covers everything else: no date, an unparseable
   * one, or one that is not in the future.
   */
  action: "none" | "create" | "update";
  /** The appointment to move, when there is one. */
  appointmentId: string | null;
  /**
   * The window the appointment occupies. All four are required by the API, so
   * they are resolved here rather than at the call site — a missing one is a
   * `ValidationError` the caller would only see in a log.
   *
   * `statedStartTime` records whether the hour is hers or the fallback, so a
   * surface can tell the difference even though the appointment cannot.
   */
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  statedStartTime: boolean;
  /**
   * Patient ids to attach besides the patient. A sperm collection from the
   * partner is his appointment as much as hers — he is the one who has to be
   * somewhere — so he rides along when the two accounts are linked.
   */
  additionalParticipantIds: string[];
  /**
   * True when the sample is the partner's, the date is one worth putting in a
   * calendar, and no partner account is linked to carry it. The surface turns
   * this into an invitation to connect him, never a dead end.
   */
  invitePartnerToConnect: boolean;
};

const NOTHING: ProcedureAppointmentPlan = {
  action: "none",
  appointmentId: null,
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null,
  statedStartTime: false,
  additionalParticipantIds: [],
  invitePartnerToConnect: false,
};

/**
 * Splits the value the step stores — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm:ss` — the
 * same way `usePhases` does on the way to the API, so the appointment and the
 * phase cannot disagree about the hour. A midnight time is treated as no time:
 * it is what a date-only picker leaves behind, and an appointment at 00:00 is a
 * fabricated hour rather than a stated one.
 */
function splitStepDate(value: unknown): {
  date: string | null;
  time: string | null;
} {
  if (typeof value !== "string" || value.length < 10) {
    return { date: null, time: null };
  }
  const [datePart, timePart] = value.split("T");
  if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return { date: null, time: null };
  }
  const time = timePart ? timePart.slice(0, 5) : null;
  return {
    date: datePart,
    time: time && /^\d{2}:\d{2}$/.test(time) && time !== "00:00" ? time : null,
  };
}

export function resolveProcedureAppointmentPlan(input: {
  stepId: string;
  /** The step's buffered data, as the modal saves it. */
  data: Record<string, string | number | boolean | null | undefined>;
  /** The step's own date field — `procedureDate`, `transferDate`, … */
  dateField: string;
  /** Today, `YYYY-MM-DD`, in the patient's own reckoning. */
  todayIso: string;
  /** The zone the window is measured in; the device's, at the call site. */
  timeZone?: string | null;
  /**
   * The partner relationship as `resolvePartnerCounterparty` reports it.
   *
   * Not `patient.partnerId`: that module says outright that `partnerId` is a
   * legacy display field and "cannot establish who the current relationship is
   * with", so using it here risked attaching a former partner to a live
   * appointment. Its four states also carry the distinction this decision needs
   * — `unresolved` means the projection has not loaded, which is not the same as
   * `none`, and offering to invite a partner she may already have is a worse
   * answer than offering nothing yet.
   */
  partner?: {
    state: "unresolved" | "none" | "resolved" | "ambiguous";
    patientId: string | null;
  } | null;
}): ProcedureAppointmentPlan {
  const { date, time } = splitStepDate(input.data[input.dateField]);
  if (!date) return NOTHING;

  const isPartnerSample =
    input.stepId === "sperm_collection" && input.data.spermSource === "partner";

  // Strictly later. A collection today is happening today: she is not planning
  // it any more, and the schedule row is already gone by then.
  if (date <= input.todayIso) return NOTHING;

  const appointmentId =
    typeof input.data.appointmentId === "string" && input.data.appointmentId
      ? input.data.appointmentId
      : null;

  const startTime = time ?? DEFAULT_START_TIME;
  const end = calculateAppointmentEnd({
    startDate: date,
    startTime,
    durationMinutes: DEFAULT_DURATION_MINUTES,
    timeZone: input.timeZone ?? null,
  });
  // The helper only fails on input it cannot parse, and the date and time above
  // are already known-good — but an appointment with half a window is one the API
  // refuses, so there is nothing to gain by sending it.
  if (!end) return NOTHING;

  const linkedPartnerId =
    input.partner?.state === "resolved" ? input.partner.patientId : null;

  return {
    action: appointmentId ? "update" : "create",
    appointmentId,
    startDate: date,
    startTime,
    endDate: end.endDate,
    endTime: end.endTime,
    statedStartTime: time !== null,
    additionalParticipantIds:
      isPartnerSample && linkedPartnerId ? [linkedPartnerId] : [],
    // Only when we know there is no partner. `unresolved` has not loaded and
    // `ambiguous` is a relationship the app cannot name — neither is an invitation
    // to send.
    invitePartnerToConnect: isPartnerSample && input.partner?.state === "none",
  };
}
